<script lang="ts">
  import { project } from "$lib/services/store";
  import { onMount } from "svelte";
  import { getBucketLink } from "$lib/common/helpers/image";
  import { page } from "$app/stores";
  import { isTournamentsRoute } from "$lib/services/utils";
  import type { BlockStyling } from "@/src/types/components/Styling";

  interface ImageData {
    url?: string;
    alt?: string;
    caption?: string;
    border?: boolean;
    hideOnMobile?: boolean;
    hideOnDesktop?: boolean;
    linkUrl?: string;
    openNewWindow?: boolean;
    addNofollow?: boolean;
    captionOutside?: boolean;
    singleCaption?: boolean;
    width?: number;
  }

  interface Props {
    imageBlockType?: string;
    images?: ImageData[];
    content?: string;
    styling?: BlockStyling;
  }

  export let props: Props = {};
  export let containerRef: HTMLElement | null = null;

  const tournamentsRoute = $project.settings.tournamentsUrl ?? "turneringar";
  const IMG_CONTENT_FOLDER = getBucketLink() + "media/";

  // This is a workaround to add data-attributes to the image links since we add them with CMS block
  onMount(() => {
    if (containerRef) {
      const links = containerRef.querySelectorAll("a");

      links.forEach((link: Element, index: number) => {
        const href = link.getAttribute("href");
        if (href && href.includes("turneringar")) {
          link.setAttribute("data-element", "click_tournament");
          link.setAttribute("data-placement", "banner");
        }
      });
    }
  });
</script>

<div class="content-grid {props?.styling?.marginTop ?? ''} {props?.styling?.marginBottom ?? ''}" id="block-image">
  <div bind:this={containerRef} class="block-image">
    {#if props.images?.length}
      {#if props.imageBlockType === "single" || !props.imageBlockType || props.imageBlockType === "link"}
        {#if props.images[0].border}
          <div class="border image-holder" class:caption-outside={props.images[0].captionOutside}>
            {#if props.imageBlockType === "link" && props.images[0].linkUrl}
              <a
                href={props.images[0].linkUrl}
                target={props.images[0].openNewWindow ? "_blank" : null}
                rel={props.images[0].addNofollow ? "nofollow" : null}>
                <img
                  class={"in-content lazyload"}
                  loading="lazy"
                  alt={props.images[0].alt}
                  data-src={IMG_CONTENT_FOLDER + props.images[0].url}
                  src={IMG_CONTENT_FOLDER + props.images[0].url} />
              </a>
            {:else}
              <img
                class={"in-content lazyload"}
                loading="lazy"
                alt={props.images[0].alt}
                data-src={IMG_CONTENT_FOLDER + props.images[0].url}
                src={IMG_CONTENT_FOLDER + props.images[0].url} />
            {/if}
            {#if props.images[0].caption && !props.images[0].captionOutside}
              <p class="img-caption">{@html props.images[0].caption}</p>
            {/if}
          </div>
        {:else}
          <div
            class="image-holder"
            class:caption-outside={props.images[0].captionOutside}
            class:hide-mobile={props.images[0].hideOnMobile}
            class:hide-desktop={props.images[0].hideOnDesktop}>
            {#if props.imageBlockType === "link" && props.images[0].linkUrl}
              <a
                href={props.images[0].linkUrl}
                target={props.images[0].openNewWindow ? "_blank" : null}
                rel={props.images[0].addNofollow ? "nofollow" : null}>
                <img
                  class={"in-content lazyload"}
                  loading="lazy"
                  alt={props.images[0].alt}
                  data-src={IMG_CONTENT_FOLDER + props.images[0].url}
                  src={IMG_CONTENT_FOLDER + props.images[0].url} />
              </a>
            {:else}
              <img
                class={"in-content lazyload"}
                loading="lazy"
                alt={props.images[0].alt}
                data-src={IMG_CONTENT_FOLDER + props.images[0].url}
                src={IMG_CONTENT_FOLDER + props.images[0].url} />
            {/if}
            {#if props.images[0].caption && !props.images[0].captionOutside}
              <p class="img-caption">{@html props.images[0].caption}</p>
            {/if}
          </div>
        {/if}
        {#if props.images[0].caption && props.images[0].captionOutside}
          <p class="img-caption">{@html props.images[0].caption}</p>
        {/if}
      {:else if props.imageBlockType === "content"}
        <div
          class="flex flex-col align-center md:flex-row-reverse"
          style="--image-width: {props.images[0].width || 50}%;">
          <div
            class="image-holder"
            class:hide-mobile={props.images[0].hideOnMobile}
            class:hide-desktop={props.images[0].hideOnDesktop}>
            <img
              class={"in-content lazyload"}
              class:border={props.images[0].border}
              loading="lazy"
              alt={props.images[0].alt}
              data-src={IMG_CONTENT_FOLDER + props.images[0].url}
              src={IMG_CONTENT_FOLDER + props.images[0].url} />
            {#if props.images[0].caption}
              <p class="img-caption">{@html props.images[0].caption}</p>
            {/if}
          </div>
          <div
            style="--text-width: {100 - props.images[0].width}%;"
            class="text-holder"
            class:tournament-page={isTournamentsRoute($page.url.pathname, tournamentsRoute)}>
            {#if props.content}
              {@html props.content}
            {/if}
          </div>
        </div>
      {:else if props.imageBlockType === "multi" || props.imageBlockType === "carousel"}
        <div class={props.imageBlockType}>
          {#each props.images as image, i}
            <div class="image-holder" class:hide-mobile={image.hideOnMobile} class:hide-desktop={image.hideOnDesktop}>
              <img
                class={"in-content lazyload"}
                class:border={image.border}
                loading="lazy"
                alt={image.alt}
                data-src={IMG_CONTENT_FOLDER + image.url + "?w=600&q=80&auto=format"}
                src={IMG_CONTENT_FOLDER + image.url + "?w=600&q=80&auto=format"} />
              {#if image.caption && !props.images[0].singleCaption}
                <p class="img-caption">{@html image.caption}</p>
              {/if}
            </div>
          {/each}
        </div>
        {#if props.images[0].caption && props.images[0].singleCaption}
          <p class="img-caption single">{@html props.images[0].caption}</p>
        {/if}
      {/if}
    {/if}
  </div>
</div>

<style>
  .block-link {
    & img {
      margin-bottom: 5px;
    }
  }
  .block-padding {
    padding: 12px 0;
  }

  .block-image {
    .image-holder {
      width: 100%;
      @media (min-width: 768px) {
        width: var(--image-width);
      }
    }
    .text-holder {
      width: 100%;
      @media (min-width: 768px) {
        width: var(--text-width);
      }
    }
  }

  .dark-mode.image-holder.left,
  .dark-mode.image-holder.right {
    width: 100%;
    margin: 0 auto;

    @media (min-width: 768px) {
      max-width: min(450px, 60%);
    }
  }
  @container window (min-width: 450px) {
    .dark-mode.image-holder.left {
      float: left;
      margin: 0;
      @media (min-width: 768px) {
        margin-right: 1rem;
      }
    }
    .dark-mode.image-holder.right {
      float: right;
      margin: 0;

      @media (min-width: 768px) {
        margin-left: 1rem;
      }
    }
  }
  :global(.tournament-page a) {
    text-decoration: underline;
    text-decoration-color: #cc3129 !important;
    text-underline-offset: 8px;
    text-decoration-thickness: 3px;
    color: white;

    &:hover {
      color: lightgray;
    }
  }
  :global(.tournament-page p) {
    margin: 12px 0;
  }

  .hide-mobile {
    @media only screen and (max-width: 990px) {
      display: none !important;
    }
  }

  .hide-desktop {
    @media only screen and (min-width: 992px) {
      display: none !important;
    }
  }
</style>
